/**
 * t565u: changing a building issue's due date (set, change or clear) after it was raised. The checks both apps run
 * before Save, the request body, and the plain en-GB lines the server posts in the issue's discussion, e.g.
 * "Due date changed from Thu 29 Oct to Mon 2 Nov". Times are always Europe/London. Pure TS.
 */
import { DateTime } from 'luxon';
import { formatInstantGB } from '../gbDate';
import type { IncidentStatus } from './incidents';

const ZONE = 'Europe/London';
/** The time a due date gets when only a day is chosen and it had none before. */
export const ISSUE_DUE_DEFAULT_TIME = '17:00';
const OPEN_STATUSES: IncidentStatus[] = ['new', 'acknowledged', 'in_progress', 'on_hold'];

const day = (iso: string) => formatInstantGB(new Date(iso), 'EEE d MMM');
const dayTime = (iso: string) => formatInstantGB(new Date(iso), 'EEE d MMM, HH:mm');
const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) : null);

/** "Thu 29 Oct, 17:00" for the issue page. */
export const issueDueText = (iso: string) => dayTime(iso);

/** The one-line change, e.g. "Due date changed from Thu 29 Oct to Mon 2 Nov". Same day: the times are shown. */
export function issueDueChange(from: string | null, to: string | null): string {
  if (ms(from) === ms(to)) return '';
  if (!from && to) return `Due date set to ${day(to)}`;
  if (from && !to) return `Due date removed (was ${day(from)})`;
  const sameDay = day(from!) === day(to!);
  return sameDay ? `Due date changed from ${dayTime(from!)} to ${dayTime(to!)}` : `Due date changed from ${day(from!)} to ${day(to!)}`;
}

/**
 * The discussion line after the person's name (t706u: one phrase, no repeat): "set the due date to Mon 2 Nov",
 * "changed the due date to Mon 2 Nov (was Thu 29 Oct)", "removed the due date (was Thu 29 Oct)". Same day: with times.
 */
export function issueDueComment(from: string | null, to: string | null): string {
  if (ms(from) === ms(to)) return '';
  if (!from && to) return `set the due date to ${day(to)}`;
  if (from && !to) return `removed the due date (was ${day(from)})`;
  const f = day(from!) === day(to!) ? dayTime : day;
  return `changed the due date to ${f(to!)} (was ${f(from!)})`;
}

/** Managers can change the due date while the issue is open. */
export const issueDueEditable = (i: { status: IncidentStatus; can_manage?: boolean }) => Boolean(i.can_manage) && OPEN_STATUSES.includes(i.status);

// ------------------------------------------------------------------------------------------------ the form
/** Day (yyyy-mm-dd) and time (HH:mm) in UK time; an empty day means no due date. */
export interface IssueDueForm { day: string; time: string }

export function issueDueForm(resolve_due_at: string | null | undefined): IssueDueForm {
  if (!resolve_due_at) return { day: '', time: '' };
  const d = DateTime.fromISO(new Date(resolve_due_at).toISOString()).setZone(ZONE);
  return { day: d.toISODate()!, time: d.toFormat('HH:mm') };
}

/** The instant the form stands for (null = no due date). */
export function issueDueValue(f: IssueDueForm): string | null {
  if (!f.day) return null;
  const d = DateTime.fromISO(`${f.day}T${f.time || ISSUE_DUE_DEFAULT_TIME}`, { zone: ZONE });
  return d.isValid ? d.toUTC().toISO() : null;
}

/** The same checks as the server, so Save says what's wrong before it posts. */
export function issueDueProblem(f: IssueDueForm, now = new Date()): string | null {
  if (!f.day) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.day) || !DateTime.fromISO(f.day).isValid) return 'Choose a day';
  if (f.time && !/^\d{2}:\d{2}$/.test(f.time)) return 'Choose a time';
  const v = issueDueValue(f);
  if (!v) return 'Choose a day';
  if (Date.parse(v) < now.getTime() - 365 * 86400_000 || Date.parse(v) > now.getTime() + 5 * 365 * 86400_000) return 'Choose a day within the next few years';
  return null;
}

/** PATCH /ops/incidents/:id/due body, or null when nothing changed. */
export function issueDuePayload(f: IssueDueForm, was: string | null | undefined): { resolve_due_at: string | null } | null {
  const v = issueDueValue(f);
  return ms(v) === ms(was ?? null) ? null : { resolve_due_at: v };
}
